import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, ArrowUpRight, Check } from "lucide-react";
import { content } from "../i18n/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setStatus("success");
      setForm({ name: "", email: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  const field =
    "w-full bg-transparent border-b border-[#2a2626]/25 focus:border-[#c86f53] outline-none py-4 text-base text-[#2a2626] placeholder:text-[#5c5656]/50 font-light transition-colors duration-500";

  return (
    <section id="contact" className="relative py-28 sm:py-40 bg-[#f2ebe1]" data-testid="contact-section">
      <div className="max-w-[1500px] mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-[11px] uppercase tracking-[0.35em] text-[#c86f53] mb-8"
            data-testid="contact-chapter-label"
          >
            {content.contact.chapter}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#2a2626] font-medium mb-3"
            data-testid="contact-heading"
          >
            {content.contact.headingDe}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="font-display italic text-2xl sm:text-3xl text-[#6b705c] font-light mb-8"
            data-testid="contact-heading-en"
          >
            {content.contact.headingEn}
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="max-w-md mb-12"
            data-testid="contact-subtext"
          >
            <p className="text-sm sm:text-base leading-relaxed text-[#5c5656] font-light">{content.contact.subDe}</p>
            <p className="mt-2 font-display italic text-base sm:text-lg leading-relaxed text-[#6b705c] font-light">
              {content.contact.subEn}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="space-y-8"
          >
            <a href={`tel:${content.contact.phone.replace("(0) ", "").replace(/[^+0-9]/g, "")}`} className="group flex items-center gap-5" data-testid="contact-phone-link">
              <span className="w-12 h-12 rounded-full border border-[#2a2626]/20 flex items-center justify-center text-[#2a2626] transition-colors duration-500 group-hover:bg-[#c86f53] group-hover:border-[#c86f53] group-hover:text-[#f9f6f0]">
                <Phone size={16} strokeWidth={1.5} />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.3em] text-[#5c5656]">{content.contact.phoneLabel}</span>
                <span className="font-display text-xl sm:text-2xl text-[#2a2626]">{content.contact.phone}</span>
              </span>
            </a>
            <a href={`mailto:${content.contact.email}`} className="group flex items-center gap-5" data-testid="contact-email-link">
              <span className="w-12 h-12 rounded-full border border-[#2a2626]/20 flex items-center justify-center text-[#2a2626] transition-colors duration-500 group-hover:bg-[#c86f53] group-hover:border-[#c86f53] group-hover:text-[#f9f6f0]">
                <Mail size={16} strokeWidth={1.5} />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.3em] text-[#5c5656]">{content.contact.emailLabel}</span>
                <span className="font-display text-xl sm:text-2xl text-[#2a2626] break-all">{content.contact.email}</span>
              </span>
            </a>
          </motion.div>
        </div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-6 lg:col-start-7 bg-[#f9f6f0] border border-[#2a2626]/10 p-8 sm:p-12 lg:p-16"
          data-testid="contact-form"
        >
          <div className="space-y-8">
            <div>
              <label htmlFor="contact-name" className="block text-[10px] uppercase tracking-[0.28em] text-[#5c5656] mb-1">
                {content.contact.nameLabel}
              </label>
              <input
                id="contact-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={field}
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-[10px] uppercase tracking-[0.28em] text-[#5c5656] mb-1">
                {content.contact.emailFieldLabel}
              </label>
              <input
                id="contact-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={field}
                data-testid="contact-email-input"
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-[10px] uppercase tracking-[0.28em] text-[#5c5656] mb-1">
                {content.contact.messageLabel}
              </label>
              <textarea
                id="contact-message"
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`${field} resize-none`}
                data-testid="contact-message-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={status === "sending"}
            className="group mt-12 inline-flex items-center gap-3 bg-[#2a2626] text-[#f9f6f0] rounded-full px-10 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-[#c86f53] disabled:opacity-50 transition-colors duration-500"
            data-testid="contact-submit-button"
          >
            {status === "sending" ? content.contact.sending : content.contact.submit}
            <ArrowUpRight size={14} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>

          {status === "success" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 flex items-start gap-3"
              data-testid="contact-success-message"
            >
              <Check size={16} className="mt-0.5 shrink-0 text-[#6b705c]" />
              <div>
                <p className="text-sm text-[#6b705c]">{content.contact.successDe}</p>
                <p className="font-display italic text-base text-[#6b705c]/75">{content.contact.successEn}</p>
              </div>
            </motion.div>
          )}
          {status === "error" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8"
              data-testid="contact-error-message"
            >
              <p className="text-sm text-[#c86f53]">{content.contact.errorDe}</p>
              <p className="font-display italic text-base text-[#c86f53]/80">{content.contact.errorEn}</p>
            </motion.div>
          )}
        </motion.form>
      </div>
    </section>
  );
};
